/**
 * Dashboard Analytics Hook
 * Provides easy-to-use analytics methods for AI Telemetry Dashboard components
 * Built on top of the AnalyticsProvider
 */

import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useAnalytics as useAnalyticsProvider } from '../providers/AnalyticsProvider';
import type { AnalyticsEvent } from '../providers/AnalyticsProvider';

export function useAnalytics() {
  const analytics = useAnalyticsProvider();
  const location = useLocation();
  const navigationType = useNavigationType();
  const lastPageRef = useRef<string>('');
  const pageStartTimeRef = useRef<number>(Date.now());

  // Auto-track page views when location changes
  useEffect(() => {
    const currentPage = location.pathname;
    const pageTitle = document.title;

    // Track page view
    analytics.trackPageView(currentPage, {
      page_title: pageTitle,
      navigation_type: navigationType,
      from_page: lastPageRef.current,
      search_params: location.search,
    });

    // Track time spent on previous page
    if (lastPageRef.current && lastPageRef.current !== currentPage) {
      const timeSpent = Date.now() - pageStartTimeRef.current;
      analytics.trackPerformanceMetric('page_time_spent', timeSpent, 'ms', {
        page: lastPageRef.current,
      });
    }

    lastPageRef.current = currentPage;
    pageStartTimeRef.current = Date.now();
  }, [location, navigationType, analytics]);

  // Dashboard-specific analytics methods
  const dashboardAnalytics = {
    // Authentication & User Events
    auth: {
      login: (method: 'email' | 'sso' | 'magic_link', success: boolean, properties?: AnalyticsEvent) => {
        analytics.trackEvent('auth_login_attempt', {
          auth_method: method,
          success,
          ...properties,
        });
      },
      logout: () => {
        analytics.trackEvent('auth_logout');
      },
      registration: (method: string, success: boolean) => {
        analytics.trackEvent('auth_registration', {
          registration_method: method,
          success,
        });
      },
      sessionTimeout: () => {
        analytics.trackEvent('auth_session_timeout');
      },
    },

    // Navigation & Dashboard Views
    navigation: {
      sidebarClick: (destination: string) => {
        analytics.trackUserAction('sidebar_navigation', 'sidebar', {
          destination,
        });
      },
      dashboardSwitch: (fromDashboard: string, toDashboard: string) => {
        analytics.trackDashboardView(toDashboard, {
          from_dashboard: fromDashboard,
        });
      },
      breadcrumbClick: (level: number, destination: string) => {
        analytics.trackUserAction('breadcrumb_click', 'breadcrumb', {
          level,
          destination,
        });
      },
    },

    // Data & Telemetry Analytics
    telemetry: {
      traceViewed: (traceId: string, properties?: AnalyticsEvent) => {
        analytics.trackDataQuery('trace_viewed', {
          trace_id: traceId,
          ...properties,
        });
      },
      metricQueried: (metricType: string, timeRange: string, properties?: AnalyticsEvent) => {
        analytics.trackDataQuery('metric_queried', {
          metric_type: metricType,
          time_range: timeRange,
          ...properties,
        });
      },
      dataExported: (exportType: string, dataSize: number, format: string) => {
        analytics.trackUserAction('data_exported', 'export_button', {
          export_type: exportType,
          data_size: dataSize,
          format,
        });
      },
      filterApplied: (filterType: string, filterValue: string) => {
        analytics.trackUserAction('filter_applied', 'filter_controls', {
          filter_type: filterType,
          filter_value: filterValue,
        });
      },
      searchPerformed: (searchTerm: string, resultCount: number) => {
        analytics.trackDataQuery('telemetry_search', {
          search_term: searchTerm,
          result_count: resultCount,
        });
      },
    },

    // Experiments & A/B Testing
    experiments: {
      experimentCreated: (experimentType: string, properties?: AnalyticsEvent) => {
        analytics.trackExperimentAction('experiment_created', {
          experiment_type: experimentType,
          ...properties,
        });
      },
      experimentStarted: (experimentId: string) => {
        analytics.trackExperimentAction('experiment_started', {
          experiment_id: experimentId,
        });
      },
      experimentStopped: (experimentId: string, reason: string) => {
        analytics.trackExperimentAction('experiment_stopped', {
          experiment_id: experimentId,
          stop_reason: reason,
        });
      },
      experimentResultsViewed: (experimentId: string) => {
        analytics.trackExperimentAction('experiment_results_viewed', {
          experiment_id: experimentId,
        });
      },
    },

    // Deployments & CI/CD
    deployments: {
      deploymentTriggered: (environment: string, deploymentType: string) => {
        analytics.trackDeploymentAction('deployment_triggered', {
          environment,
          deployment_type: deploymentType,
        });
      },
      rollbackPerformed: (deploymentId: string, reason: string) => {
        analytics.trackDeploymentAction('rollback_performed', {
          deployment_id: deploymentId,
          rollback_reason: reason,
        });
      },
      pullRequestReviewed: (action: 'approved' | 'rejected' | 'commented') => {
        analytics.trackDeploymentAction('pull_request_reviewed', {
          review_action: action,
        });
      },
    },

    // Integrations
    integrations: {
      lakefsAction: (action: string, properties?: AnalyticsEvent) => {
        analytics.trackIntegrationUsage('lakefs', action, properties);
      },
      appsyncConnection: (success: boolean, errorType?: string) => {
        analytics.trackIntegrationUsage('appsync', 'connect', {
          success,
          error_type: errorType,
        });
      },
      apiEndpointCalled: (endpoint: string, method: string, statusCode: number, responseTime: number) => {
        analytics.trackPerformanceMetric('api_call', responseTime, 'ms', {
          endpoint,
          method,
          status_code: statusCode,
        });
      },
    },

    // User Interface & Experience
    ui: {
      modalOpened: (modalType: string) => {
        analytics.trackUserAction('modal_opened', 'modal', {
          modal_type: modalType,
        });
      },
      tooltipViewed: (tooltipId: string) => {
        analytics.trackUserAction('tooltip_viewed', 'tooltip', {
          tooltip_id: tooltipId,
        });
      },
      chartInteraction: (chartType: string, interaction: string) => {
        analytics.trackUserAction('chart_interaction', 'chart', {
          chart_type: chartType,
          interaction,
        });
      },
      settingsChanged: (settingName: string, newValue: string) => {
        analytics.trackUserAction('settings_changed', 'settings', {
          setting_name: settingName,
          new_value: newValue,
        });
      },
    },

    // Performance & Errors
    performance: {
      componentLoadTime: (componentName: string, loadTime: number) => {
        analytics.trackLoadTime(componentName, loadTime);
      },
      apiResponseTime: (endpoint: string, responseTime: number) => {
        analytics.trackPerformanceMetric('api_response_time', responseTime, 'ms', {
          endpoint,
        });
      },
      chunkLoadFailure: (chunkName: string, error: Error) => {
        analytics.trackError(error, 'chunk_load_failure', {
          chunk_name: chunkName,
        });
      },
    },

    // Business Intelligence
    business: {
      featureAdoption: (featureName: string, adoptionStage: 'discovered' | 'tried' | 'adopted') => {
        if (adoptionStage === 'discovered') {
          analytics.trackFeatureDiscovery(featureName, 'organic');
        } else {
          analytics.trackFeatureUsage(featureName, {
            adoption_stage: adoptionStage,
          });
        }
      },
      conversionEvent: (conversionType: string, value?: number) => {
        analytics.trackConversion(conversionType, {
          conversion_value: value,
        });
      },
      retentionMilestone: (milestone: string) => {
        analytics.trackBusinessMetric('user_retention', 1, {
          milestone,
        });
      },
    },
  };

  // Performance monitoring hooks
  const useComponentLoadTime = useCallback((componentName: string) => {
    const startTime = useRef<number>(Date.now());

    useEffect(() => {
      return () => {
        const loadTime = Date.now() - startTime.current;
        dashboardAnalytics.performance.componentLoadTime(componentName, loadTime);
      };
    }, [componentName]);
  }, []);

  // Error boundary integration
  const trackErrorBoundary = useCallback((error: Error, errorInfo: { componentStack: string }) => {
    analytics.trackError(error, 'error_boundary', {
      component_stack: errorInfo.componentStack,
    });
  }, [analytics]);

  return {
    // Direct access to base analytics methods
    ...analytics,

    // Dashboard-specific analytics
    ...dashboardAnalytics,

    // Performance hooks
    useComponentLoadTime,
    trackErrorBoundary,
  };
}

// Hook for tracking form interactions
export function useFormAnalytics(formName: string) {
  const { trackUserAction } = useAnalytics();

  return {
    trackFormStart: () => trackUserAction('form_started', 'form', { form_name: formName }),
    trackFormSubmit: (success: boolean, errors?: string[]) =>
      trackUserAction('form_submitted', 'form', {
        form_name: formName,
        success,
        errors: errors?.join(','),
      }),
    trackFieldInteraction: (fieldName: string, action: 'focus' | 'blur' | 'change') =>
      trackUserAction('form_field_interaction', 'form_field', {
        form_name: formName,
        field_name: fieldName,
        action,
      }),
  };
}

// Hook for tracking table/list interactions
export function useTableAnalytics(tableName: string) {
  const { trackUserAction } = useAnalytics();

  return {
    trackSort: (column: string, direction: 'asc' | 'desc') =>
      trackUserAction('table_sorted', 'table', {
        table_name: tableName,
        column,
        direction,
      }),
    trackFilter: (filterType: string, filterValue: string) =>
      trackUserAction('table_filtered', 'table', {
        table_name: tableName,
        filter_type: filterType,
        filter_value: filterValue,
      }),
    trackRowSelection: (selectedCount: number) =>
      trackUserAction('table_rows_selected', 'table', {
        table_name: tableName,
        selected_count: selectedCount,
      }),
    trackPagination: (page: number, pageSize: number) =>
      trackUserAction('table_paginated', 'table', {
        table_name: tableName,
        page,
        page_size: pageSize,
      }),
  };
}